import { useAiContext } from '@/hooks/useAiContext';
import { useAiEditorBridge } from '@/hooks/useAiEditorBridge';
import { cn } from '@/lib/utils';
import * as fileService from '@/services/file.service';
import { useDeployStore, useWorkspaceStore } from '@/store';
import {
    useAiAssistantStore,
    type AiMessage,
    type AiProposalAction,
} from '@/store/aiAssistantStore';
import { useEditorStore } from '@/store/editorStore';
import { notifyAiAction } from '@/store/notificationStore';
import {
    AlertTriangle,
    Bot,
    Check,
    ChevronDown,
    Code2,
    Copy,
    Eye,
    FileCode,
    FilePlus2,
    PlusSquare,
    RefreshCw,
    Replace,
    Send,
    Shield,
    Sparkles,
    TestTubes,
    Trash2,
    User,
    Wand2,
    Wrench,
    Zap,
} from 'lucide-react';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type FormEvent,
    type KeyboardEvent,
} from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import ProposalDiffDialog from './ProposalDiffDialog';

/* ================================================================== */
/*  Quick Actions                                                     */
/* ================================================================== */

const QUICK_ACTIONS = [
  { label: 'Generate', task: 'contract_template', icon: Wand2, color: 'text-purple-400' },
  { label: 'Explain', task: 'code_explain', icon: Code2, color: 'text-blue-400' },
  { label: 'Audit', task: 'security_audit', icon: Shield, color: 'text-red-400' },
  { label: 'Gas', task: 'gas_optimize', icon: Zap, color: 'text-yellow-400' },
  { label: 'Test', task: 'test_generate', icon: TestTubes, color: 'text-green-400' },
] as const;

const TASK_PROMPTS: Record<string, string> = {
  contract_template: 'Generate a smart contract based on my open file',
  code_explain: 'Explain the current code in detail',
  security_audit: 'Run a security audit on this code',
  gas_optimize: 'Analyze gas usage and suggest optimizations',
  test_generate: 'Generate comprehensive tests for this contract',
};

/* ================================================================== */
/*  Message Component                                                 */
/* ================================================================== */

interface MessageBubbleProps {
  msg: AiMessage;
  activeFilePath?: string | null;
  activeFileContent?: string;
  onInsertCode: (code: string, language: string) => void;
  onCreateFile: (code: string, language: string, targetPath?: string) => void;
  onApplyCode: (code: string, language: string) => void;
  onAppendCode: (code: string, language: string) => void;
  onApplyAndRecompile?: (msg: AiMessage) => Promise<boolean>;
  onApplyAndRunTests?: (msg: AiMessage) => Promise<boolean>;
  onUpdateProposalStatus: (
    messageId: string,
    patch: {
      applied?: 'success' | 'error';
      compile?: 'success' | 'error';
      tests?: 'success' | 'error';
    },
  ) => void;
}

function MessageBubble({
  msg,
  activeFilePath,
  activeFileContent = '',
  onInsertCode,
  onCreateFile,
  onApplyCode,
  onAppendCode,
  onApplyAndRecompile,
  onApplyAndRunTests,
  onUpdateProposalStatus,
}: MessageBubbleProps) {
  const [copied, setCopied] = useState(false);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [isApplyingAndRecompiling, setIsApplyingAndRecompiling] = useState(false);
  const [isApplyingAndTesting, setIsApplyingAndTesting] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showMoreWorkflowActions, setShowMoreWorkflowActions] = useState(false);
  const preferredTarget = msg.proposal?.targetPath ?? activeFilePath ?? null;
  const proposalKindMeta = msg.proposal
    ? {
        contract: {
          labelClassName: 'bg-fuchsia-500/10 text-fuchsia-200',
          cardClassName: 'border-fuchsia-500/25 bg-fuchsia-500/5',
          icon: FileCode,
        },
        patch: {
          labelClassName: 'bg-amber-500/10 text-amber-200',
          cardClassName: 'border-amber-500/25 bg-amber-500/5',
          icon: Wrench,
        },
        test: {
          labelClassName: 'bg-emerald-500/10 text-emerald-200',
          cardClassName: 'border-emerald-500/25 bg-emerald-500/5',
          icon: TestTubes,
        },
        suggestion: {
          labelClassName: 'bg-sky-500/10 text-sky-200',
          cardClassName: 'border-sky-500/25 bg-sky-500/5',
          icon: Sparkles,
        },
        dapp: {
          labelClassName: 'bg-indigo-500/10 text-indigo-200',
          cardClassName: 'border-indigo-500/25 bg-indigo-500/5',
          icon: Code2,
        },
      }[msg.proposal.kind]
    : null;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [msg.content]);

  const handleProposalAction = useCallback(
    (action: AiProposalAction) => {
      if (!msg.proposal?.code) return;
      if (action === 'replace') {
        onApplyCode(msg.proposal.code, msg.proposal.language);
        notifyAiAction(
          'AI Proposal Applied',
          activeFilePath ? `Replaced ${activeFilePath}` : 'Replaced the active file',
          'success',
        );
        setLastAction('Applied to active file');
      } else if (action === 'append') {
        onAppendCode(msg.proposal.code, msg.proposal.language);
        notifyAiAction(
          'AI Proposal Applied',
          activeFilePath ? `Appended to ${activeFilePath}` : 'Appended to the active file',
          'success',
        );
        setLastAction('Appended to active file');
      } else {
        onCreateFile(msg.proposal.code, msg.proposal.language, msg.proposal.targetPath);
        notifyAiAction(
          'AI Proposal Applied',
          msg.proposal.targetPath
            ? `Created ${msg.proposal.targetPath}`
            : 'Created a new file from the proposal',
          'success',
        );
        setLastAction('Created a new file');
      }
      onUpdateProposalStatus(msg.id, { applied: 'success' });
      setShowMoreActions(false);
      setTimeout(() => setLastAction(null), 2200);
    },
    [
      msg.id,
      msg.proposal,
      onApplyCode,
      onAppendCode,
      onCreateFile,
      activeFilePath,
      onUpdateProposalStatus,
    ],
  );

  const handleApplyAndRecompile = useCallback(async () => {
    if (!msg.proposal?.code || !onApplyAndRecompile) return;
    setIsApplyingAndRecompiling(true);
    try {
      const ok = await onApplyAndRecompile(msg);
      notifyAiAction(
        ok ? 'AI Proposal Applied' : 'AI Proposal Not Applied',
        ok
          ? activeFilePath
            ? `Updated ${activeFilePath} and started recompilation`
            : 'Updated the active file and started recompilation'
          : 'Unable to apply the proposal before recompiling',
        ok ? 'success' : 'error',
      );
      setLastAction(ok ? 'Applied and recompiled' : 'Unable to recompile');
      setTimeout(() => setLastAction(null), 2200);
    } catch (error) {
      notifyAiAction(
        'AI Proposal Not Applied',
        error instanceof Error ? error.message : 'Unable to apply the proposal before recompiling',
        'error',
      );
      setLastAction('Unable to recompile');
      setTimeout(() => setLastAction(null), 2200);
    } finally {
      setIsApplyingAndRecompiling(false);
    }
  }, [msg, onApplyAndRecompile, activeFilePath, onUpdateProposalStatus]);

  const handleApplyAndRunTests = useCallback(async () => {
    if (!msg.proposal || !onApplyAndRunTests) return;
    setIsApplyingAndTesting(true);
    try {
      const ok = await onApplyAndRunTests(msg);
      notifyAiAction(
        ok ? 'AI Proposal Applied' : 'AI Proposal Not Applied',
        ok
          ? msg.proposal.kind === 'test'
            ? msg.proposal.targetPath
              ? `Created ${msg.proposal.targetPath} and started tests`
              : 'Created a new test file and started tests'
            : activeFilePath
              ? `Updated ${activeFilePath} and started tests`
              : 'Updated the active file and started tests'
          : 'Unable to apply the proposal before running tests',
        ok ? 'success' : 'error',
      );
      setLastAction(ok ? 'Applied and ran tests' : 'Unable to run tests');
      setTimeout(() => setLastAction(null), 2200);
    } catch (error) {
      notifyAiAction(
        'AI Proposal Not Applied',
        error instanceof Error
          ? error.message
          : 'Unable to apply the proposal before running tests',
        'error',
      );
      setLastAction('Unable to run tests');
      setTimeout(() => setLastAction(null), 2200);
    } finally {
      setIsApplyingAndTesting(false);
    }
  }, [msg, onApplyAndRunTests, activeFilePath, onUpdateProposalStatus]);

  if (msg.role === 'system') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-[#252526] rounded text-[11px] text-[#858585] italic">
        <Sparkles className="w-3 h-3 shrink-0" />
        {msg.content}
      </div>
    );
  }

  const isUser = msg.role === 'user';
  const Icon = isUser ? User : Bot;
  const iconBg = isUser ? 'bg-blue-500/20' : 'bg-purple-500/20';
  const iconColor = isUser ? 'text-blue-400' : 'text-purple-400';
  const preferredActionLabels: Record<AiProposalAction, string> = {
    replace: 'Replace active file',
    append: 'Append to active file',
    'new-file': 'Create new file',
    scaffold: 'Scaffold project files',
  };
  const actionButtons: Array<{
    action: AiProposalAction;
    label: string;
    title: string;
    icon: typeof Replace;
    disabled: boolean;
    className: string;
  }> = [
    {
      action: 'replace' as AiProposalAction,
      label:
        msg.proposal?.preferredAction === 'replace'
          ? (msg.proposal.actionLabel ?? 'Apply to Active File')
          : 'Replace Active File',
      title: 'Replace the active file with this AI proposal',
      icon: Replace,
      disabled: !activeFilePath,
      className:
        msg.proposal?.preferredAction === 'replace'
          ? 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_0_1px_rgba(192,132,252,0.35)]'
          : 'bg-yellow-600/20 text-yellow-300 hover:bg-yellow-600/35',
    },
    {
      action: 'append' as AiProposalAction,
      label:
        msg.proposal?.preferredAction === 'append'
          ? (msg.proposal.actionLabel ?? 'Append to Active File')
          : 'Append to Active File',
      title: 'Append this AI proposal to the active file',
      icon: PlusSquare,
      disabled: !activeFilePath,
      className:
        msg.proposal?.preferredAction === 'append'
          ? 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_0_1px_rgba(192,132,252,0.35)]'
          : 'bg-blue-600/20 text-blue-300 hover:bg-blue-600/35',
    },
    {
      action: 'new-file' as AiProposalAction,
      label:
        msg.proposal?.preferredAction === 'new-file'
          ? (msg.proposal.actionLabel ?? 'Create New File')
          : 'Create New File',
      title: 'Create a new file with this AI proposal',
      icon: FilePlus2,
      disabled: false,
      className:
        msg.proposal?.preferredAction === 'new-file'
          ? 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_0_1px_rgba(192,132,252,0.35)]'
          : 'bg-green-600/20 text-green-300 hover:bg-green-600/35',
    },
  ].sort((left, right) => {
    if (left.action === msg.proposal?.preferredAction) return -1;
    if (right.action === msg.proposal?.preferredAction) return 1;
    return 0;
  });
  const primaryActionButton = actionButtons[0];
  const secondaryActionButtons = actionButtons.slice(1);
  const workflowButtons: Array<{
    key: 'recompile' | 'tests' | 'diff';
    label: string;
    title: string;
    icon: typeof RefreshCw;
    onClick: () => void;
    disabled: boolean;
    className: string;
  }> = [
    ...(msg.proposal?.runCompileAfterApply && onApplyAndRecompile
      ? [
          {
            key: 'recompile' as const,
            label: 'Apply and Recompile',
            title: 'Replace the active file, save it, and re-run compile',
            icon: RefreshCw,
            onClick: () => {
              setShowMoreWorkflowActions(false);
              void handleApplyAndRecompile();
            },
            disabled: !activeFilePath || isApplyingAndRecompiling,
            className:
              'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_0_1px_rgba(192,132,252,0.35)]',
          },
        ]
      : []),
    ...(msg.proposal?.runTestsAfterApply && onApplyAndRunTests
      ? [
          {
            key: 'tests' as const,
            label: 'Apply and Run Tests',
            title: 'Apply the proposal and run tests in the active workspace',
            icon: RefreshCw,
            onClick: () => {
              setShowMoreWorkflowActions(false);
              void handleApplyAndRunTests();
            },
            disabled:
              isApplyingAndTesting ||
              (msg.proposal.preferredAction === 'replace' && !activeFilePath),
            className: 'bg-green-700/20 text-green-300 hover:bg-green-700/35',
          },
        ]
      : []),
    {
      key: 'diff',
      label: 'Preview Diff',
      title: 'Preview the current file against the AI proposal',
      icon: Eye,
      onClick: () => {
        setShowMoreWorkflowActions(false);
        setShowDiff(true);
      },
      disabled: false,
      className: 'bg-[#3b3b3b] text-[#d4d4d4] hover:bg-[#4a4a4a]',
    },
  ];
  const primaryWorkflowButton = workflowButtons[0];
  const secondaryWorkflowButtons = workflowButtons.slice(1);

  return (
    <div className={cn('group flex gap-2', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5',
          iconBg,
        )}
      >
        <Icon className={cn('w-3.5 h-3.5', iconColor)} />
      </div>
      <div className={cn('flex-1 min-w-0', isUser && 'text-right')}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] text-[#858585]">{isUser ? 'You' : 'KuberNodes AI'}</span>
          {msg.task && msg.task !== 'chat' && (
            <span className="px-1 py-px bg-purple-600/20 text-purple-400 rounded text-[9px]">
              {msg.task.replace(/_/g, ' ')}
            </span>
          )}
          {!isUser && (
            <button
              onClick={handleCopy}
              className="ml-auto p-0.5 opacity-0 group-hover:opacity-100 text-[#858585] hover:text-white transition-all"
              title="Copy response"
            >
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            </button>
          )}
        </div>
        <div
          className={cn(
            'rounded-lg px-3 py-2',
            isUser
              ? 'bg-blue-600/20 text-[#d4d4d4] text-xs whitespace-pre-wrap break-words inline-block text-left'
              : 'bg-[#252526] border border-[#333]',
          )}
        >
          {isUser ? (
            msg.content
          ) : (
            <MarkdownRenderer
              content={msg.content}
              onInsertCode={onInsertCode}
              onCreateFile={onCreateFile}
              onApplyCode={onApplyCode}
            />
          )}
        </div>
        {!isUser && msg.proposal && (
          <div
            className={cn(
              'mt-2 rounded-lg border p-2.5',
              proposalKindMeta?.cardClassName ?? 'border-purple-500/20 bg-purple-500/5',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#f1f5f9]">
                  {proposalKindMeta && <proposalKindMeta.icon className="h-3.5 w-3.5" />}
                  {msg.proposal.title}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-[#9ca3af]">
                  <span>
                    {preferredTarget
                      ? `Target: ${preferredTarget}`
                      : 'Review and choose where to apply this code'}
                  </span>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                      proposalKindMeta?.labelClassName ?? 'bg-purple-500/10 text-purple-200',
                    )}
                  >
                    {msg.proposal.kind}
                  </span>
                  <span className="rounded bg-[#ffffff10] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[#d4d4d4]">
                    Preferred: {preferredActionLabels[msg.proposal.preferredAction]}
                  </span>
                  {msg.proposalStatus?.applied && (
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                        msg.proposalStatus.applied === 'success'
                          ? 'bg-cyan-500/10 text-cyan-200'
                          : 'bg-red-500/10 text-red-200',
                      )}
                    >
                      {msg.proposalStatus.applied === 'success' ? 'applied' : 'apply failed'}
                    </span>
                  )}
                  {msg.proposalStatus?.compile && (
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                        msg.proposalStatus.compile === 'success'
                          ? 'bg-violet-500/10 text-violet-200'
                          : 'bg-red-500/10 text-red-200',
                      )}
                    >
                      {msg.proposalStatus.compile === 'success' ? 'compiled' : 'compile failed'}
                    </span>
                  )}
                  {msg.proposalStatus?.tests && (
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                        msg.proposalStatus.tests === 'success'
                          ? 'bg-emerald-500/10 text-emerald-200'
                          : 'bg-red-500/10 text-red-200',
                      )}
                    >
                      {msg.proposalStatus.tests === 'success' ? 'tested' : 'tests failed'}
                    </span>
                  )}
                </div>
              </div>
              {lastAction && <span className="text-[10px] text-green-400">{lastAction}</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {primaryWorkflowButton &&
                (() => {
                  const WorkflowIcon = primaryWorkflowButton.icon;
                  return (
                    <button
                      onClick={primaryWorkflowButton.onClick}
                      disabled={primaryWorkflowButton.disabled}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                        primaryWorkflowButton.className,
                      )}
                      title={primaryWorkflowButton.title}
                    >
                      <WorkflowIcon
                        className={cn(
                          'h-3 w-3',
                          primaryWorkflowButton.key === 'recompile' &&
                            isApplyingAndRecompiling &&
                            'animate-spin',
                          primaryWorkflowButton.key === 'tests' &&
                            isApplyingAndTesting &&
                            'animate-spin',
                        )}
                      />
                      {primaryWorkflowButton.label}
                    </button>
                  );
                })()}
              {secondaryWorkflowButtons.length > 0 && (
                <button
                  onClick={() => setShowMoreWorkflowActions((current) => !current)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#2c2c2c] px-2 py-1 text-[10px] text-[#c7c7c7] transition-colors hover:bg-[#3a3a3a]"
                  title="Show more workflow actions"
                >
                  <ChevronDown
                    className={cn(
                      'h-3 w-3 transition-transform',
                      showMoreWorkflowActions && 'rotate-180',
                    )}
                  />
                  More workflow actions
                </button>
              )}
              {primaryActionButton &&
                (() => {
                  const ActionIcon = primaryActionButton.icon;
                  return (
                    <button
                      onClick={() => handleProposalAction(primaryActionButton.action)}
                      disabled={primaryActionButton.disabled}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                        primaryActionButton.className,
                      )}
                      title={primaryActionButton.title}
                    >
                      <ActionIcon className="h-3 w-3" />
                      {primaryActionButton.label}
                    </button>
                  );
                })()}
              {secondaryActionButtons.length > 0 && (
                <button
                  onClick={() => setShowMoreActions((current) => !current)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#2c2c2c] px-2 py-1 text-[10px] text-[#c7c7c7] transition-colors hover:bg-[#3a3a3a]"
                  title="Show more proposal actions"
                >
                  <ChevronDown
                    className={cn('h-3 w-3 transition-transform', showMoreActions && 'rotate-180')}
                  />
                  More actions
                </button>
              )}
            </div>
            {showMoreWorkflowActions && secondaryWorkflowButtons.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5 border-t border-purple-500/10 pt-1.5">
                {secondaryWorkflowButtons.map((workflowButton) => {
                  const WorkflowIcon = workflowButton.icon;
                  return (
                    <button
                      key={workflowButton.key}
                      onClick={workflowButton.onClick}
                      disabled={workflowButton.disabled}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                        workflowButton.className,
                      )}
                      title={workflowButton.title}
                    >
                      <WorkflowIcon
                        className={cn(
                          'h-3 w-3',
                          workflowButton.key === 'recompile' &&
                            isApplyingAndRecompiling &&
                            'animate-spin',
                          workflowButton.key === 'tests' && isApplyingAndTesting && 'animate-spin',
                        )}
                      />
                      {workflowButton.label}
                    </button>
                  );
                })}
              </div>
            )}
            {showMoreActions && secondaryActionButtons.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5 border-t border-purple-500/10 pt-1.5">
                {secondaryActionButtons.map((actionButton) => {
                  const ActionIcon = actionButton.icon;
                  return (
                    <button
                      key={actionButton.action}
                      onClick={() => handleProposalAction(actionButton.action)}
                      disabled={actionButton.disabled}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                        actionButton.className,
                      )}
                      title={actionButton.title}
                    >
                      <ActionIcon className="h-3 w-3" />
                      {actionButton.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {!isUser && msg.proposal && (
          <ProposalDiffDialog
            isOpen={showDiff}
            title={msg.proposal.title}
            currentLabel={activeFilePath ? `Current: ${activeFilePath}` : 'Current File'}
            proposedLabel="AI Proposal"
            currentContent={activeFileContent}
            proposedContent={msg.proposal.code}
            onClose={() => setShowDiff(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

export default function AiAssistantPanel() {
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    appendMessage,
    updateProposalStatus,
    snapshotCode,
    clearMessages,
    clearError,
  } = useAiAssistantStore();
  const { activeTabId, openFiles, tabs, updateFileContent, saveFile } = useEditorStore();
  const { createFileFromAi, replaceActiveFile, appendToActiveFile } = useAiEditorBridge();
  const { activeWorkspace } = useWorkspaceStore();
  const { compile, runTests, toolchainInfo } = useDeployStore();
  const { getAiContext } = useAiContext();
  const [input, setInput] = useState('');
  const [showActions, setShowActions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const workspaceId = activeWorkspace?.id ?? null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  // Use the richer project context hook (includes multi-file context + workspace)
  // instead of the simplified single-file version that was here before.
  const getActiveFileContext = useCallback(
    (): Record<string, unknown> => getAiContext(),
    [getAiContext],
  );

  const handleInsertCode = useCallback(
    (code: string, _language: string) => {
      const tab = tabs.find((t) => t.id === activeTabId);
      if (!tab) return;
      const existingContent = openFiles.get(tab.path) ?? '';
      const newContent = existingContent + '\n\n' + code;
      updateFileContent(tab.path, newContent);
    },
    [activeTabId, openFiles, tabs, updateFileContent],
  );

  const handleCreateFile = useCallback(
    (code: string, _language: string, targetPath?: string) => {
      createFileFromAi(code, targetPath);
    },
    [createFileFromAi],
  );

  const handleApplyCode = useCallback(
    (code: string, _language: string) => {
      // Snapshot before replacing so the user can undo
      const tab = tabs.find((t) => t.id === activeTabId);
      if (tab) {
        const current = openFiles.get(tab.path) ?? '';
        snapshotCode(tab.path, current, 'Before: Apply Code');
      }
      replaceActiveFile(code);
    },
    [replaceActiveFile, tabs, activeTabId, openFiles, snapshotCode],
  );

  const handleAppendCode = useCallback(
    (code: string, _language: string) => {
      appendToActiveFile(code);
    },
    [appendToActiveFile],
  );

  const handleApplyAndRecompile = useCallback(
    async (msg: AiMessage) => {
      const tab = tabs.find((t) => t.id === activeTabId);
      if (!tab || !workspaceId) return false;

      // Snapshot before replacing
      const currentContent = openFiles.get(tab.path) ?? '';
      snapshotCode(tab.path, currentContent, `Before: ${msg.proposal?.title ?? 'Apply Fix'}`);

      const applied = replaceActiveFile(msg.proposal?.code ?? '');
      if (!applied) {
        updateProposalStatus(msg.id, { applied: 'error', compile: 'error' });
        return false;
      }

      await fileService.saveFile(workspaceId, tab.path, msg.proposal?.code ?? '');
      saveFile(tab.path);

      const toolchain = toolchainInfo?.foundry
        ? 'foundry'
        : toolchainInfo?.hardhat
          ? 'hardhat'
          : undefined;
      await compile(workspaceId, toolchain);

      const result = useDeployStore.getState().compileResult;
      updateProposalStatus(msg.id, {
        applied: 'success',
        compile: result?.success ? 'success' : 'error',
      });
      if (result?.success) {
        appendMessage({
          role: 'assistant',
          task: 'fix_error',
          content: `## Recompile Successful\n\n${tab.path} compiled successfully.${result.warnings?.length ? `\n\nWarnings: ${result.warnings.length}` : ''}`,
        });
      } else if (result) {
        const errors = result.errors
          ?.slice(0, 3)
          .map((entry) => `- ${entry.message}`)
          .join('\n');
        appendMessage({
          role: 'assistant',
          task: 'fix_error',
          content: `## Recompile Failed\n\nThe proposal was applied, but compilation still failed.${errors ? `\n\n${errors}` : ''}`,
        });
      }

      return true;
    },
    [
      activeTabId,
      workspaceId,
      tabs,
      openFiles,
      replaceActiveFile,
      saveFile,
      toolchainInfo,
      compile,
      appendMessage,
      updateProposalStatus,
      snapshotCode,
    ],
  );

  const handleApplyAndRunTests = useCallback(
    async (msg: AiMessage) => {
      if (!workspaceId || !msg.proposal) return false;

      if (msg.proposal.preferredAction === 'replace') {
        const tab = tabs.find((t) => t.id === activeTabId);
        if (!tab) {
          updateProposalStatus(msg.id, { applied: 'error', tests: 'error' });
          return false;
        }

        const applied = replaceActiveFile(msg.proposal.code);
        if (!applied) {
          updateProposalStatus(msg.id, { applied: 'error', tests: 'error' });
          return false;
        }

        await fileService.saveFile(workspaceId, tab.path, msg.proposal.code);
        saveFile(tab.path);
      } else if (msg.proposal.preferredAction === 'new-file') {
        await createFileFromAi(msg.proposal.code, msg.proposal.targetPath);
      } else {
        return false;
      }

      const toolchain = toolchainInfo?.foundry
        ? 'foundry'
        : toolchainInfo?.hardhat
          ? 'hardhat'
          : undefined;

      await runTests(workspaceId, {
        toolchain,
        ...(msg.proposal.kind === 'test' && msg.proposal.targetPath
          ? { testFile: msg.proposal.targetPath }
          : {}),
      });

      const result = useDeployStore.getState().testResult;
      updateProposalStatus(msg.id, {
        applied: 'success',
        tests: result?.success ? 'success' : 'error',
      });
      if (result) {
        appendMessage({
          role: 'assistant',
          task: msg.task,
          content: result.success
            ? `## Test Run Successful\n\n${result.passed} passed, ${result.failed} failed${result.skipped > 0 ? `, ${result.skipped} skipped` : ''}.`
            : `## Test Run Failed\n\n${result.passed} passed, ${result.failed} failed${result.skipped > 0 ? `, ${result.skipped} skipped` : ''}.`,
        });
      }

      return true;
    },
    [
      workspaceId,
      tabs,
      activeTabId,
      replaceActiveFile,
      saveFile,
      createFileFromAi,
      toolchainInfo,
      runTests,
      appendMessage,
      updateProposalStatus,
    ],
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || isLoading) return;
      setInput('');
      setShowActions(false);
      sendMessage(trimmed, 'chat', getActiveFileContext());
    },
    [input, isLoading, sendMessage, getActiveFileContext],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  const handleQuickAction = useCallback(
    (task: string) => {
      setShowActions(false);
      const context = getActiveFileContext();
      const prompt = TASK_PROMPTS[task] ?? `Run ${task}`;
      sendMessage(prompt, task, context);
    },
    [getActiveFileContext, sendMessage],
  );

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const activeFileContent = activeTab ? (openFiles.get(activeTab.path) ?? '') : '';

  return (
    <div className="flex flex-col h-full bg-[#1e1e1e] text-[#cccccc]">
      {/* Header */}
      <div className="flex items-center justify-between h-9 px-3 border-b border-[#2d2d2d] shrink-0">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#bbbbbb]">
          <Sparkles className="w-3.5 h-3.5 text-purple-400" />
          AI Assistant
        </div>
        <button
          onClick={() => {
            clearMessages();
            setShowActions(true);
          }}
          className="p-1 text-[#858585] hover:text-white transition-colors"
          title="Clear chat"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Quick Actions (collapsible) */}
      <div className="border-b border-[#2d2d2d] shrink-0">
        <button
          onClick={() => setShowActions(!showActions)}
          className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] text-[#858585] hover:text-white transition-colors"
        >
          <span>Quick Actions</span>
          <ChevronDown
            className={cn('w-3 h-3 transition-transform', showActions && 'rotate-180')}
          />
        </button>
        {showActions && (
          <div className="flex flex-wrap gap-1.5 px-3 pb-2">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.task}
                  onClick={() => handleQuickAction(action.task)}
                  disabled={isLoading}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] rounded-md bg-[#2d2d2d] hover:bg-[#3d3d3d] text-[#cccccc] whitespace-nowrap transition-colors disabled:opacity-50 border border-transparent hover:border-[#555]"
                >
                  <Icon className={cn('w-3 h-3', action.color)} />
                  {action.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            activeFilePath={activeTab?.path ?? null}
            activeFileContent={activeFileContent}
            onInsertCode={handleInsertCode}
            onCreateFile={handleCreateFile}
            onApplyCode={handleApplyCode}
            onAppendCode={handleAppendCode}
            onApplyAndRecompile={handleApplyAndRecompile}
            onApplyAndRunTests={handleApplyAndRunTests}
            onUpdateProposalStatus={updateProposalStatus}
          />
        ))}

        {isLoading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-md bg-purple-500/20 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-purple-400 animate-pulse" />
            </div>
            <div className="bg-[#252526] border border-[#333] rounded-lg px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-[#858585]">
                <span className="animate-pulse">Thinking</span>
                <span className="flex gap-0.5">
                  <span
                    className="w-1 h-1 bg-purple-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-purple-400 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-1 h-1 bg-purple-400 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={clearError} className="text-[10px] underline hover:no-underline">
              dismiss
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-2 border-t border-[#2d2d2d] shrink-0">
        {activeTab && (
          <div className="flex items-center gap-1.5 mb-1.5 px-1">
            <FileCode className="w-3 h-3 text-[#666]" />
            <span className="text-[9px] text-[#555] truncate">{activeTab.path}</span>
            <span className="text-[9px] text-[#444]">({activeTab.language})</span>
          </div>
        )}
        <div className="flex items-end gap-1.5 bg-[#2d2d2d] rounded-lg px-3 py-2 border border-[#444] focus-within:border-purple-500/50 transition-colors">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              activeTab
                ? `Ask about ${activeTab.name}...`
                : 'Ask about Web3, Solidity, or generate a contract...'
            }
            rows={1}
            disabled={isLoading}
            className="flex-1 bg-transparent text-xs text-[#d4d4d4] placeholder-[#666] outline-none resize-none max-h-[120px] min-h-[20px]"
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="p-1.5 text-[#858585] hover:text-purple-400 transition-colors disabled:opacity-30 rounded hover:bg-purple-600/10"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-[9px] text-[#444] px-1">Shift+Enter for new line</p>
      </form>
    </div>
  );
}
